import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  CheckCircle2,
  FileText,
  Layers,
  Loader2,
  Sparkles,
} from 'lucide-react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  getDocHubMediaUrl,
  getDocHubPdfPreviewUrl,
} from '../services/price-intel-service';

export interface DocHubDocumentContentDoc {
  id: string;
  name: string;
  type: string;
  extractedText: string;
  docId?: string;
  fileUrl?: string;
  fileBlob?: File;
}

interface DocHubDocumentSummaryCard {
  headline: string;
  keyPoints: string[];
}

interface PresentationSlideContent {
  pageNumber: number;
  markdown: string;
}

export interface DocHubDocumentContentProps {
  selectedDoc: DocHubDocumentContentDoc;
  viewMode: 'rendered' | 'original' | 'source';
  docSummary: DocHubDocumentSummaryCard | null;
  onViewModeChange: (mode: 'rendered' | 'original' | 'source') => void;
  onImagePreview: (preview: { src: string; alt: string }) => void;
}

function allowMediaUrlTransform(url: string): string {
  return url.startsWith('media:') ? url : defaultUrlTransform(url);
}

const OfficePreview: React.FC<{ file: File; fileName: string }> = ({ file, fileName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    let cancelled = false;
    let viewerInstance: { destroy?: () => void } | undefined;
    container.innerHTML = '';
    setError(null);
    setLoading(true);

    const ext = fileName.toLowerCase().slice(fileName.lastIndexOf('.'));

    (async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        if (cancelled) return;

        if (ext === '.docx' || ext === '.doc') {
          const docxPreview = await import('docx-preview');
          await docxPreview.renderAsync(arrayBuffer, container, undefined, {
            className: 'docx-preview-wrapper',
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreFonts: false,
            breakPages: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
          });
        } else if (ext === '.pptx') {
          await document.fonts.ready;
          if (cancelled) return;
          const { PPTXViewer } = await import('pptx-viewer');
          const viewer = new PPTXViewer(container);
          viewerInstance = viewer;
          await viewer.load(arrayBuffer);
        } else if (ext === '.ppt' || ext === '.odp') {
          throw new Error('该格式需要服务端生成 PDF 预览，不能使用浏览器 PPTX 渲染器');
        }
        if (!cancelled) setLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('[OfficePreview] render error:', err);
        setError(err instanceof Error ? err.message : '渲染失败');
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      viewerInstance?.destroy?.();
      container.innerHTML = '';
    };
  }, [file, fileName]);

  return (
    <div className="relative flex-1 overflow-auto">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
          <div className="flex items-center gap-2 text-xs text-[var(--color-key)]">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--color-key)] border-t-transparent" />
            正在渲染原始文件...
          </div>
        </div>
      )}
      {error && <div className="p-4 text-xs text-red-500">渲染失败: {error}</div>}
      <div ref={containerRef} className="min-h-full w-full" />
    </div>
  );
};

const PresentationPreview: React.FC<{
  docId: string;
  file?: File;
  fileName: string;
}> = ({ docId, file, fileName }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const response = await fetch(getDocHubPdfPreviewUrl(docId));
        if (!response.ok) {
          const message = await response.text().catch(() => '');
          throw new Error(message || `PDF 预览生成失败 (${response.status})`);
        }
        const previewBlob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(previewBlob);
        setPreviewUrl(objectUrl);
      } catch (error) {
        if (!cancelled) {
          setPreviewError((error as Error).message || 'PDF 预览生成失败');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docId]);

  if (previewUrl) {
    return (
      <object data={previewUrl} type="application/pdf" className="h-full w-full border-none">
        <iframe src={previewUrl} className="h-full w-full border-none" title={`${fileName} PDF 预览`} />
      </object>
    );
  }

  if (previewError) {
    const canUseBrowserFallback = fileName.toLowerCase().endsWith('.pptx') && file;
    return canUseBrowserFallback ? (
      <div className="flex h-full flex-col">
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-700">
          服务端 PDF 预览不可用，已降级为浏览器近似预览。复杂字体和版式可能存在偏差。
        </div>
        <OfficePreview file={file} fileName={fileName} />
      </div>
    ) : (
      <div className="flex h-full items-center justify-center p-8 text-center text-xs text-red-500">
        {previewError}
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center gap-2 text-xs text-[var(--color-key)]">
      <Loader2 size={16} className="animate-spin" />
      正在生成高保真 PDF 预览，首次打开可能需要数秒...
    </div>
  );
};

function DocHubDocumentContentComponent({
  selectedDoc,
  viewMode,
  docSummary,
  onViewModeChange,
  onImagePreview,
}: DocHubDocumentContentProps) {
  const processedMarkdown = useMemo(() => {
    if (!selectedDoc.extractedText) return '';
    let text = selectedDoc.extractedText;
    if (!text.includes('<!-- page:1 -->') && !text.includes('<!-- page: 1 -->')) {
      text = '<!-- page:1 -->\n' + text;
    }
    return text.replace(
      /<!--\s*page:(\d+)\s*-->/g,
      '\n\n###### page-anchor:$1\n\n> 📄 **第 $1 页 (Page $1)**\n\n',
    );
  }, [selectedDoc.extractedText]);

  const presentationSlides = useMemo<PresentationSlideContent[]>(() => {
    if (selectedDoc.type !== 'ppt' || !selectedDoc.extractedText) return [];

    const sourceMarkdown = selectedDoc.extractedText;
    const pageMarkers = [...sourceMarkdown.matchAll(/<!--\s*page:(\d+)\s*-->/g)];
    if (pageMarkers.length === 0) {
      return [{ pageNumber: 1, markdown: sourceMarkdown.trim() }];
    }

    return pageMarkers.map((marker, markerIndex) => {
      const pageNumber = Number.parseInt(marker[1], 10);
      const contentStart = (marker.index ?? 0) + marker[0].length;
      const nextMarker = pageMarkers[markerIndex + 1];
      const contentEnd = nextMarker?.index ?? sourceMarkdown.length;
      const markdown = sourceMarkdown
        .slice(contentStart, contentEnd)
        .replace(/<!--\s*image-insight-(?:start(?:ing)?|end):[^>]+-->/g, '')
        .replace(/^\s*##\s*(?:📄\s*)?幻灯片\s+\d+\s*/i, '')
        .replace(/^\s*---\s*/i, '')
        .trim();

      return { pageNumber, markdown };
    });
  }, [selectedDoc.extractedText, selectedDoc.type]);

  const resolveMediaSrc = useCallback(
    (src?: string): string => {
      if (!src) return '';
      if (src.startsWith('media:') && selectedDoc.docId) {
        return getDocHubMediaUrl(selectedDoc.docId, src.slice('media:'.length));
      }
      return src;
    },
    [selectedDoc.docId],
  );

  return (
    <div className="flex-1 space-y-6 overflow-y-auto p-6">
      {docSummary && (
        <div className="rounded-2xl border border-[rgba(0,113,227,0.2)] bg-[rgba(0,113,227,0.03)] p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-[var(--color-key)]">
            <Sparkles size={14} />
            AI 智能内容简报 & 结构化摘要 (Executive Summary)
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed text-[var(--color-system-primary)]">
            {docSummary.headline}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {docSummary.keyPoints.map((point, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1 rounded-lg border border-[var(--color-key)]/20 bg-[var(--color-background-primary)] px-2.5 py-1 text-[10px] font-semibold text-[var(--color-key)] shadow-xs"
              >
                <CheckCircle2 size={12} className="text-emerald-500" />
                {point}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between border-b border-[var(--color-system-quaternary)] pb-3 text-[11px] text-[var(--color-system-secondary)]">
          <span className="font-semibold text-[var(--color-system-primary)]">
            {viewMode === 'rendered'
              ? selectedDoc.type === 'ppt'
                ? '幻灯片结构化分页阅读 (Structured Slide Reading)'
                : '解析产物富文本交互视图 (Rendered Rich Document)'
              : 'Markdown 原始源码 (Raw Content)'}
          </span>
          <span className="rounded bg-[var(--color-background-secondary)] px-2 py-0.5 text-[10px]">
            页码定位导航
          </span>
        </div>

        {viewMode === 'rendered' ? (
          <div className="prose prose-sm max-w-none dark:prose-invert" data-doc-render-view>
            {selectedDoc.type === 'ppt' ? (
              <div className="not-prose space-y-8">
                <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2.5 text-[10px] leading-relaxed text-blue-700">
                  <Layers size={14} className="mt-0.5 shrink-0" />
                  <span>
                    当前为按幻灯片分页的结构化阅读视图，保留页边界、文字、图片和表格顺序；
                    精确坐标、字体及图层请切换到“原始文件视图”核对。
                  </span>
                </div>
                {presentationSlides.map((slide) => (
                  <section
                    id={`doc-page-${slide.pageNumber}`}
                    key={slide.pageNumber}
                    data-presentation-slide
                    className="scroll-mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-[0_14px_40px_rgba(15,23,42,0.10)]"
                  >
                    <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2.5">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-700">
                        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-600 text-[9px] font-bold text-white">
                          {slide.pageNumber}
                        </span>
                        幻灯片 {slide.pageNumber}
                      </div>
                      <span className="text-[9px] text-slate-400">结构化内容层</span>
                    </div>
                    <div className="min-h-[220px] bg-white p-6 sm:p-8">
                      <div className="prose prose-sm mx-auto max-w-none">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          urlTransform={allowMediaUrlTransform}
                          components={{
                            table({ children }) {
                              return (
                                <div className="my-4 overflow-x-auto rounded-lg border border-slate-200">
                                  <table className="w-full border-collapse text-left text-[11px]">{children}</table>
                                </div>
                              );
                            },
                            th({ children }) {
                              return (
                                <th className="whitespace-nowrap border border-slate-200 bg-slate-50 px-3 py-2 font-semibold">
                                  {children}
                                </th>
                              );
                            },
                            td({ children }) {
                              return (
                                <td className="border border-slate-200 px-3 py-2 align-top">{children}</td>
                              );
                            },
                            p({ children }) {
                              return <p className="my-2 text-[12px] leading-6 text-slate-700">{children}</p>;
                            },
                            li({ children }) {
                              return <li className="my-1 text-[12px] leading-5 text-slate-700">{children}</li>;
                            },
                            blockquote({ children }) {
                              return (
                                <div className="my-2 border-l-2 border-slate-200 pl-2 text-[10px] leading-4 text-slate-500 [&_p]:my-0.5 [&_strong]:font-semibold [&_strong]:text-slate-600">
                                  {children}
                                </div>
                              );
                            },
                            img({ src, alt }) {
                              const rawSrc = typeof src === 'string' ? src : undefined;
                              const resolvedSrc = resolveMediaSrc(rawSrc);
                              const mediaFilename = rawSrc?.startsWith('media:')
                                ? rawSrc.slice('media:'.length)
                                : undefined;
                              return (
                                <span className="my-4 block text-center">
                                  <img
                                    src={resolvedSrc}
                                    alt={alt || '幻灯片配图'}
                                    data-media-filename={mediaFilename}
                                    onClick={() =>
                                      onImagePreview({ src: resolvedSrc, alt: alt || '幻灯片配图' })
                                    }
                                    title="点击放大查看"
                                    className="mx-auto max-h-[220px] max-w-full cursor-zoom-in rounded-lg border border-slate-200 object-contain shadow-sm transition hover:shadow-md"
                                  />
                                  {alt && <span className="mt-1 block text-[9px] text-slate-400">{alt}</span>}
                                </span>
                              );
                            },
                          }}
                        >
                          {slide.markdown}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                urlTransform={allowMediaUrlTransform}
                components={{
                  blockquote({ children }) {
                    return (
                      <div className="my-5 flex items-center gap-3">
                        <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[var(--color-system-quaternary)] to-transparent" />
                        <div className="flex items-center gap-1.5 rounded-full border border-[var(--color-key)]/20 bg-[rgba(0,113,227,0.06)] px-3 py-1 text-[11px] font-bold text-[var(--color-key)] shadow-xs">
                          {children}
                        </div>
                        <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[var(--color-system-quaternary)] to-transparent" />
                      </div>
                    );
                  },
                  table({ children }) {
                    return (
                      <div className="my-4 overflow-x-auto rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)] shadow-sm">
                        <table className="w-full border-collapse text-left text-xs">{children}</table>
                      </div>
                    );
                  },
                  thead({ children }) {
                    return (
                      <thead className="border-b border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] font-semibold text-[var(--color-system-primary)]">
                        {children}
                      </thead>
                    );
                  },
                  th({ children }) {
                    return (
                      <th className="whitespace-nowrap border-r border-[var(--color-system-quaternary)] px-4 py-3 text-xs last:border-r-0">
                        {children}
                      </th>
                    );
                  },
                  td({ children }) {
                    return (
                      <td className="border-r border-t border-[var(--color-system-quaternary)] px-4 py-2.5 font-sans text-xs text-[var(--color-system-primary)] last:border-r-0">
                        {children}
                      </td>
                    );
                  },
                  tr({ children }) {
                    return <tr className="transition-colors hover:bg-[rgba(0,113,227,0.03)]">{children}</tr>;
                  },
                  p({ children }) {
                    return (
                      <p className="my-2.5 text-xs leading-relaxed text-[var(--color-system-primary)]">
                        {children}
                      </p>
                    );
                  },
                  h1({ children }) {
                    return (
                      <h1 className="my-4 border-b border-[var(--color-system-quaternary)] pb-2 text-base font-bold text-[var(--color-key)]">
                        {children}
                      </h1>
                    );
                  },
                  h2({ children }) {
                    return (
                      <h2 className="my-3 text-sm font-bold text-[var(--color-system-primary)]">{children}</h2>
                    );
                  },
                  h6({ children }) {
                    const txt = children?.toString() || '';
                    if (txt.startsWith('page-anchor:')) {
                      const pageNum = txt.split(':')[1];
                      return <div id={`doc-page-${pageNum}`} className="scroll-mt-6" />;
                    }
                    return <h6 className="text-xs font-bold">{children}</h6>;
                  },
                  img({ src, alt }) {
                    const rawSrc = typeof src === 'string' ? src : undefined;
                    const resolvedSrc = resolveMediaSrc(rawSrc);
                    const mediaFilename = rawSrc?.startsWith('media:')
                      ? rawSrc.slice('media:'.length)
                      : undefined;
                    return (
                      <span className="my-3 block">
                        <img
                          src={resolvedSrc}
                          alt={alt || '文档配图'}
                          data-media-filename={mediaFilename}
                          title="点击放大查看"
                          onClick={() => onImagePreview({ src: resolvedSrc, alt: alt || '文档配图' })}
                          className="max-w-full cursor-zoom-in rounded-xl border border-[var(--color-system-quaternary)] shadow-sm transition hover:shadow-md"
                        />
                        {alt && (
                          <span className="mt-1 block text-center text-[10px] text-[var(--color-system-tertiary)]">
                            {alt}
                          </span>
                        )}
                      </span>
                    );
                  },
                  code({ children }) {
                    return (
                      <code className="rounded border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] px-1.5 py-0.5 font-mono text-[11px] text-[var(--color-key)]">
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {processedMarkdown}
              </ReactMarkdown>
            )}
          </div>
        ) : viewMode === 'original' ? (
          <div className="flex h-[650px] w-full flex-col rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 shadow-inner">
            <div className="mb-3 flex items-center justify-between border-b border-[var(--color-system-quaternary)] pb-3 text-xs font-semibold text-[var(--color-system-primary)]">
              <span className="flex items-center gap-2">
                <FileText size={16} className="text-[var(--color-key)]" />
                原版文件核对视图 ({selectedDoc.name})
              </span>
              <span className="rounded-full border border-[var(--color-key)]/20 bg-[rgba(0,113,227,0.08)] px-2.5 py-0.5 text-[10px] font-bold text-[var(--color-key)]">
                {selectedDoc.type === 'ppt' && selectedDoc.docId
                  ? '服务端 PDF 高保真预览'
                  : '原始文件预览'}
              </span>
            </div>

            {selectedDoc.type === 'ppt' && selectedDoc.docId ? (
              <div className="w-full flex-1 overflow-hidden rounded-xl border border-[var(--color-system-quaternary)] bg-white shadow-xs">
                <PresentationPreview
                  docId={selectedDoc.docId}
                  file={selectedDoc.fileBlob}
                  fileName={selectedDoc.name}
                />
              </div>
            ) : selectedDoc.type === 'pdf' ? (
              <div className="w-full flex-1 overflow-hidden rounded-xl border border-[var(--color-system-quaternary)] bg-white shadow-xs">
                {selectedDoc.fileUrl ? (
                  <object data={selectedDoc.fileUrl} type="application/pdf" className="h-full w-full border-none">
                    <iframe
                      src={selectedDoc.fileUrl}
                      className="h-full w-full border-none"
                      title="PDF 原汁原味阅读器"
                    />
                  </object>
                ) : (
                  <iframe
                    src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(
                      'https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/examples/learning/helloworld.pdf',
                    )}`}
                    className="h-full w-full border-none"
                    title="PDF 默认阅读器"
                  />
                )}
              </div>
            ) : selectedDoc.type === 'image' && selectedDoc.fileUrl ? (
              <div className="flex w-full flex-1 items-center justify-center overflow-hidden rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4">
                <img
                  src={selectedDoc.fileUrl}
                  alt={selectedDoc.name}
                  className="max-h-full max-w-full rounded-lg object-contain shadow-sm"
                />
              </div>
            ) : selectedDoc.type === 'audio' && selectedDoc.fileUrl ? (
              <div className="flex w-full flex-1 flex-col items-center justify-center gap-4 overflow-hidden rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-8">
                <div className="text-4xl">🎵</div>
                <p className="text-sm font-medium text-[var(--color-system-primary)]">{selectedDoc.name}</p>
                <audio controls src={selectedDoc.fileUrl} className="w-full max-w-md" />
              </div>
            ) : (
              <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-primary)]">
                <div className="flex items-center justify-between border-b border-[var(--color-system-quaternary)] px-3 py-2">
                  <span className="text-[10px] text-[var(--color-system-secondary)]">
                    {`${selectedDoc.type.toUpperCase()} 原始文件预览`}
                  </span>
                  <div className="flex items-center gap-2">
                    {selectedDoc.fileUrl && (
                      <a
                        href={selectedDoc.fileUrl}
                        download={selectedDoc.name}
                        className="flex items-center gap-1 rounded-md bg-[rgba(0,113,227,0.08)] px-2 py-1 text-[10px] font-medium text-[var(--color-key)] transition hover:bg-[rgba(0,113,227,0.15)]"
                      >
                        ⬇ 下载原始文件
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => onViewModeChange('rendered')}
                      className="text-[10px] text-[var(--color-key)] hover:underline"
                    >
                      富文本视图 →
                    </button>
                  </div>
                </div>
                {selectedDoc.fileBlob ? (
                  <OfficePreview file={selectedDoc.fileBlob} fileName={selectedDoc.name} />
                ) : (
                  <div className="flex-1 overflow-auto p-4">
                    <div className="prose prose-sm max-w-none [&_hr]:my-6 [&_hr]:border-[var(--color-key)]/20 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-[var(--color-system-quaternary)] [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-[var(--color-system-quaternary)] [&_th]:bg-[var(--color-background-secondary)] [&_th]:px-2 [&_th]:py-1">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedDoc.extractedText}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <pre className="whitespace-pre-wrap rounded-xl border border-[var(--color-system-quaternary)] bg-[var(--color-background-secondary)] p-4 font-mono text-xs leading-relaxed text-[var(--color-system-primary)]">
            {selectedDoc.extractedText}
          </pre>
        )}
      </div>
    </div>
  );
}

export const DocHubDocumentContent = memo(
  DocHubDocumentContentComponent,
  (previous, next) =>
    previous.selectedDoc.id === next.selectedDoc.id
    && previous.selectedDoc.extractedText === next.selectedDoc.extractedText
    && previous.selectedDoc.fileUrl === next.selectedDoc.fileUrl
    && previous.selectedDoc.fileBlob === next.selectedDoc.fileBlob
    && previous.viewMode === next.viewMode
    && previous.docSummary?.headline === next.docSummary?.headline
    && previous.docSummary?.keyPoints.join('\u0000') === next.docSummary?.keyPoints.join('\u0000'),
);
